import React, { createContext, useContext, useState, useCallback } from 'react';

// ── Traduzioni ────────────────────────────────────────────────────────────────
const TRANSLATIONS = {
  it: {
    // Sidebar
    sidebar_dashboard: 'Dashboard',
    sidebar_bookings: 'Prenotazioni',
    sidebar_requests: 'Richieste',
    sidebar_calendar: 'Calendario',
    sidebar_guests: 'Ospiti',
    sidebar_virtual_keys: 'Chiavi Virtuali',
    sidebar_devices: 'Dispositivi',
    sidebar_integrations: 'Integrazioni',
    sidebar_access_log: 'Log Accessi',
    sidebar_rates: 'Tariffe',
    sidebar_services: 'Servizi',
    sidebar_extras: 'Extra',
    sidebar_suppliers: 'Fornitori',
    sidebar_properties: 'Proprietà',
    sidebar_ai_assistant: 'AI Assistant',
    sidebar_translations: 'Testi App',
    sidebar_api_keys: 'API Keys',
    sidebar_webhooks: 'Webhook',
    sidebar_wh_analytics: 'WH Analytics',
    sidebar_admin_users: 'Utenti Admin',
    sidebar_guide: 'Guida Integrazioni',
    sidebar_useful_links: 'Link Utili',
    sidebar_msg_templates: 'Modelli Messaggi',
    sidebar_marketplace: 'Recensioni Marketplace',
    sidebar_branding: 'Branding & White-Label',
    sidebar_receipts: 'Ricevute & CRM',
    sidebar_settings: 'Impostazioni',
    // Header
    header_language: 'Lingua',
    header_logout: 'Esci',
    header_profile: 'Profilo',
    // Common
    common_save: 'Salva',
    common_cancel: 'Annulla',
    common_delete: 'Elimina',
    common_edit: 'Modifica',
    common_add: 'Aggiungi',
    common_loading: 'Caricamento...',
    common_no_data: 'Nessun dato disponibile',
    common_confirm: 'Conferma',
    common_search: 'Cerca...',
    common_filter: 'Filtra',
    common_export: 'Esporta',
    common_refresh: 'Aggiorna',
    common_close: 'Chiudi',
    // Bookings
    bookings_status_pending: 'In attesa',
    bookings_status_confirmed: 'Confermata',
    bookings_status_cancelled: 'Cancellata',
    bookings_status_revoked: 'Revocata',
    bookings_checkin: 'Check-in',
    bookings_checkout: 'Check-out',
    bookings_guests: 'Ospiti',
    bookings_total: 'Totale',
    // API
    apikeys_generate: 'Genera Chiave',
    apikeys_revoke: 'Revoca',
    apikeys_usage: 'Utilizzo',
    // Webhooks
    webhooks_add: 'Aggiungi Webhook',
    webhooks_test: 'Test',
    webhooks_logs: 'Log',
    webhooks_events: 'Eventi',
    webhooks_retry: 'Riprova',
    webhooks_retry_success: 'Replay inviato con successo',
    webhooks_retry_fail: 'Replay fallito',
    webhooks_no_logs: 'Nessun evento registrato ancora.',
    webhooks_last_events: 'Ultimi',
    webhooks_disabled: 'Disabilitato',
    webhooks_update: 'Aggiorna',
    webhooks_add_first: 'Aggiungi il primo webhook per integrare il tuo PMS o sistema di automazione.',
  },
  en: {
    // Sidebar
    sidebar_dashboard: 'Dashboard',
    sidebar_bookings: 'Bookings',
    sidebar_requests: 'Requests',
    sidebar_calendar: 'Calendar',
    sidebar_guests: 'Guests',
    sidebar_virtual_keys: 'Virtual Keys',
    sidebar_devices: 'Devices',
    sidebar_integrations: 'Integrations',
    sidebar_access_log: 'Access Log',
    sidebar_rates: 'Rates',
    sidebar_services: 'Services',
    sidebar_extras: 'Extras',
    sidebar_suppliers: 'Suppliers',
    sidebar_properties: 'Properties',
    sidebar_ai_assistant: 'AI Assistant',
    sidebar_translations: 'App Texts',
    sidebar_api_keys: 'API Keys',
    sidebar_webhooks: 'Webhooks',
    sidebar_wh_analytics: 'WH Analytics',
    sidebar_admin_users: 'Admin Users',
    sidebar_guide: 'Integration Guide',
    sidebar_useful_links: 'Useful Links',
    sidebar_msg_templates: 'Message Templates',
    sidebar_marketplace: 'Marketplace Reviews',
    sidebar_branding: 'Branding & White-Label',
    sidebar_receipts: 'Receipts & CRM',
    sidebar_settings: 'Settings',
    // Header
    header_language: 'Language',
    header_logout: 'Logout',
    header_profile: 'Profile',
    // Common
    common_save: 'Save',
    common_cancel: 'Cancel',
    common_delete: 'Delete',
    common_edit: 'Edit',
    common_add: 'Add',
    common_loading: 'Loading...',
    common_no_data: 'No data available',
    common_confirm: 'Confirm',
    common_search: 'Search...',
    common_filter: 'Filter',
    common_export: 'Export',
    common_refresh: 'Refresh',
    common_close: 'Close',
    // Bookings
    bookings_status_pending: 'Pending',
    bookings_status_confirmed: 'Confirmed',
    bookings_status_cancelled: 'Cancelled',
    bookings_status_revoked: 'Revoked',
    bookings_checkin: 'Check-in',
    bookings_checkout: 'Check-out',
    bookings_guests: 'Guests',
    bookings_total: 'Total',
    // API
    apikeys_generate: 'Generate Key',
    apikeys_revoke: 'Revoke',
    apikeys_usage: 'Usage',
    // Webhooks
    webhooks_add: 'Add Webhook',
    webhooks_test: 'Test',
    webhooks_logs: 'Logs',
    webhooks_events: 'Events',
    webhooks_retry: 'Retry',
    webhooks_retry_success: 'Replay sent successfully',
    webhooks_retry_fail: 'Replay failed',
    webhooks_no_logs: 'No events recorded yet.',
    webhooks_last_events: 'Last',
    webhooks_disabled: 'Disabled',
    webhooks_update: 'Refresh',
    webhooks_add_first: 'Add your first webhook to integrate your PMS or automation system.',
  },
};

// ── Context ───────────────────────────────────────────────────────────────────
const I18nContext = createContext({ lang: 'it', t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const stored = () => {
    try { return localStorage.getItem('admin_lang') || 'it'; } catch { return 'it'; }
  };
  const [lang, setLangState] = useState(stored);

  const setLang = useCallback((l) => {
    if (l !== 'it' && l !== 'en') return;
    localStorage.setItem('admin_lang', l);
    setLangState(l);
  }, []);

  const t = useCallback((key) => {
    return TRANSLATIONS[lang]?.[key] ?? TRANSLATIONS['it']?.[key] ?? key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, t, setLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

// ── Toggle component (da usare nell'header admin) ─────────────────────────────
export function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <button
      data-testid="lang-toggle"
      onClick={() => setLang(lang === 'it' ? 'en' : 'it')}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-all"
      style={{
        background: 'var(--lux-surface-3)',
        border: '1px solid var(--lux-border-2)',
        color: 'var(--lux-text-muted)',
        cursor: 'pointer',
      }}
      title={lang === 'it' ? 'Switch to English' : 'Passa all\'Italiano'}
    >
      <span style={{ fontSize: '14px', lineHeight: 1 }}>{lang === 'it' ? '🇮🇹' : '🇬🇧'}</span>
      <span>{lang === 'it' ? 'IT' : 'EN'}</span>
    </button>
  );
}
